/** One selectable brand logo from `assets/images/google-campaign-logos`. */
export interface BrandLogo {
  /**
   * Asset subdirectory name. This is the value persisted on the template, so it
   * is a contract with the stored data — renaming a folder orphans every
   * template that already points at it.
   */
  folder: string;
  /** Human-readable brand name, shown in the dropdown. */
  label: string;
  /** Ready-to-bind asset path. */
  path: string;
}

/**
 * The brand logos an author can put on a Demand Gen ad, and the folder->URL
 * lookup the preview uses.
 *
 * The catalog is hardcoded rather than discovered because a browser cannot list
 * a directory: the build copies `src/assets` verbatim, so the only way to know
 * what is in there is to say so here. Adding a brand means dropping the PNG in a
 * new subdirectory and adding one row below.
 *
 * Subdirectory names are deliberately ASCII slugs. They travel to the DB as-is
 * and back out into an `<img src>` resolved through Azure Static Web Apps, and
 * spaces, `&` and diacritics all misbehave somewhere along that path — a `&` in
 * a folder name would not even survive the Angular template lexer.
 */
export class BrandLogoHelper {
  private static readonly BASE_PATH = 'assets/images/google-campaign-logos';

  static readonly LOGOS: readonly BrandLogo[] = [
    this.logo('valamar-glavni', 'Valamar glavni', 'umbrella-512x512-color.png'),
    this.logo('collection', 'Collection', 'valamar-collection-512x512-color.png'),
    this.logo('hotel-and-resorts', 'Hotel & Resorts', 'valamar-vhr-512x512-color.png'),
    this.logo('valamar-camping', 'Valamar Camping', 'valamar-camping-512x512-color.png'),
    this.logo('places-brand', 'PLACES brand', 'places-512x512-color.png'),
    this.logo('places-dalmacija', 'PLACES Dalmacija', 'places-dalmacija-512x512-color.png'),
    this.logo('places-hvar', 'PLACES Hvar', 'places-hvar-512x512-color.png'),
    this.logo('places-obertauern', 'PLACES Obertauern', 'places-obertauern-512x512-color.png'),
    this.logo('maro-holiday', 'Maro Holiday', 'maro-holiday-512x512-color.png'),
    this.logo('purobeach-porec', 'Purobeach Poreč', '202605_purobeach_logo_color_512x512.png'),
    this.logo('sunny', 'Sunny', 'sunny-512x512-color.png'),
  ];

  private static readonly BY_FOLDER = new Map<string, BrandLogo>(
    this.LOGOS.map((logo) => [logo.folder, logo]),
  );

  /**
   * Asset path for a stored folder name, or '' when nothing is selected or the
   * folder is no longer in the catalog. Never falls back to another brand's
   * logo: showing the wrong advertiser is worse than showing none, and the
   * empty string is what the previews render their placeholder slot for.
   *
   * Kept to a plain map lookup — the preview calls this on every
   * change-detection pass.
   */
  static urlFor(folder: string): string {
    return this.BY_FOLDER.get(folder)?.path ?? '';
  }

  private static logo(folder: string, label: string, file: string): BrandLogo {
    return { folder, label, path: `${this.BASE_PATH}/${folder}/${file}` };
  }
}
